// src/utils/csrf.js
// ── Pure CSRF token manager ───────────────────────────────────────────────
// This module intentionally has NO axios import. All HTTP work is done by
// the caller (axios.js / AuthProvider) to avoid a circular dependency:
//   axios.js → csrf.js → axios.js (circular — breaks module evaluation)
//
// initCsrf() receives the axios instance as a parameter so this module stays
// decoupled from the transport layer entirely.
//
// Reliability rules (mobile networks drop requests often):
//   - the token fetch is retried a few times before giving up
//   - the request gate NEVER waits forever: after GATE_TIMEOUT_MS it lets the
//     request through (the server answers 403, and axios.js refreshes the
//     token and retries that request once)
//   - if a request reaches the gate while no token exists and no fetch is in
//     flight, the gate starts a fetch itself (self-healing after a failure)

export const CSRF_PATH = '/csrf-token';
export const CSRF_HEADER = 'x-csrf-token';

const GATE_TIMEOUT_MS = 8000;
const FETCH_ATTEMPTS = 3;
const FETCH_RETRY_DELAY_MS = 700;

let csrfToken = null;
let resolveCsrfReady;
let csrfReady = new Promise((resolve) => {
  resolveCsrfReady = resolve;
});

// In-flight guard: concurrent callers share one fetch.
let initPromise = null;

// Incremented by resetCsrfGate(). An in-flight fetch that started under an
// older generation discards its result instead of overwriting the new state.
let sessionGeneration = 0;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function waitWithTimeout(promise, ms) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), ms);

    promise.then(() => {
      clearTimeout(timer);
      resolve(true);
    });
  });
}

async function fetchTokenWithRetry(axiosInstance, attemptsLeft) {
  try {
    const res = await axiosInstance.get(`${CSRF_PATH}?t=${Date.now()}`, {
      _bypassCsrfGate: true,
      _skipCsrfRetry: true,
    });

    const token = res?.data?.csrfToken;

    if (!token || typeof token !== 'string') {
      throw new Error('CSRF endpoint did not return a valid csrfToken');
    }

    return token;
  } catch (error) {
    if (attemptsLeft <= 1) throw error;

    await sleep(FETCH_RETRY_DELAY_MS);
    return fetchTokenWithRetry(axiosInstance, attemptsLeft - 1);
  }
}

/**
 * Fetch the CSRF token and attach it to the given axios instance.
 * Safe to call many times — returns the cached token or the in-flight fetch.
 */
export async function initCsrf(axiosInstance) {
  if (csrfToken) return csrfToken;
  if (initPromise) return initPromise;

  const generation = sessionGeneration;

  const thisPromise = (async () => {
    try {
      const token = await fetchTokenWithRetry(axiosInstance, FETCH_ATTEMPTS);

      if (generation !== sessionGeneration) {
        console.warn('[csrf] initCsrf result discarded — session was reset mid-flight');
        return null;
      }

      csrfToken = token;
      axiosInstance.defaults.headers.common[CSRF_HEADER] = token;

      // Unblock every request that was waiting at the gate.
      resolveCsrfReady();

      return token;
    } finally {
      // Only clear the slot if a newer fetch has not replaced it.
      if (initPromise === thisPromise) {
        initPromise = null;
      }
    }
  })();

  initPromise = thisPromise;

  return thisPromise;
}

/**
 * Returns the current in-memory CSRF token (null before initCsrf runs).
 */
export function getCsrfToken() {
  return csrfToken;
}

/**
 * Clear the token and re-arm the gate.
 * Call on logout AFTER the logout POST has completed (the POST bypasses the
 * gate and must still carry the old token).
 */
export function resetCsrfGate(axiosInstance) {
  sessionGeneration += 1;

  csrfToken = null;
  initPromise = null;

  if (axiosInstance) {
    delete axiosInstance.defaults.headers.common[CSRF_HEADER];
  }

  csrfReady = new Promise((resolve) => {
    resolveCsrfReady = resolve;
  });
}

/**
 * Drop the current token and fetch a fresh one.
 * Used after login / wallet-claim (the server may rotate the session) and by
 * the 403 retry interceptor in axios.js.
 */
let refreshPromise = null;

export async function refreshCsrf(axiosInstance) {
  // Many requests can fail with 403 at the same moment — share one refresh.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      resetCsrfGate(axiosInstance);
      return await initCsrf(axiosInstance);
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Attach a request interceptor that pauses requests until a token exists.
 * Requests to the CSRF route and requests with _bypassCsrfGate skip it.
 */
export function attachCsrfGate(instance) {
  if (instance._csrfGateAttached) return;
  instance._csrfGateAttached = true;

  instance.interceptors.request.use(async (config) => {
    if (config.url?.includes(CSRF_PATH)) return config;
    if (config._bypassCsrfGate) return config;
    if (csrfToken) return config;

    // Self-heal: nobody is fetching a token right now, so start one.
    if (!initPromise) {
      initCsrf(instance).catch((error) => {
        console.warn('[csrf] background token fetch failed', error?.message || error);
      });
    }

    const ready = await waitWithTimeout(csrfReady, GATE_TIMEOUT_MS);

    if (!ready) {
      console.warn('[csrf] gate timed out — sending request without waiting further');
    }

    // Request interceptors run in reverse registration order, so the header
    // interceptor in axios.js already ran BEFORE this wait. Stamp the token
    // that arrived while we waited, otherwise this request would 403.
    applyCsrfHeader(config);

    return config;
  });
}

/**
 * Writes the current token onto a request config (no-op without a token).
 */
export function applyCsrfHeader(config) {
  if (!csrfToken || !config) return config;

  if (!config.headers) {
    config.headers = {};
  }

  if (typeof config.headers.set === 'function') {
    config.headers.set(CSRF_HEADER, csrfToken);
  } else {
    config.headers[CSRF_HEADER] = csrfToken;
  }

  return config;
}
